import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import InfoIcon from "@mui/icons-material/Info";
import FeedIcon from "@mui/icons-material/Feed";
import PermContactCalendarIcon from "@mui/icons-material/PermContactCalendar";
import SchoolIcon from "@mui/icons-material/School";

import PaperText from "../../helper-components/misc/PaperText";
import PaperFragment from "../../helper-components/misc/PaperFragment";
import TransparentFragment from "../../helper-components/misc/TransparentFragment";
import CustomForm from "../../helper-components/form/CustomForm";
import NavigationFooter from "../../helper-components/misc/NavigationFooter";
import CustomButton from "../../helper-components/input/CustomButton.jsx";

import { replacePlaceholdersWithObjValues } from "../../helper-functions/shared/miscUtils.js";

import { getLanguage } from "../../config/language/language.js";

/**
 * A simple "about us" page, which includes what this app in meant to do.
 */
export default function About(props) {
    const status = props.backend.programStatus;

    const isLoggedIn = props.user.isLoggedIn;

    const loggedInOverviewText = replacePlaceholdersWithObjValues(getLanguage().HOME_OVERVIEW_TEXT_1, status);
    const loggedOutOverviewText = replacePlaceholdersWithObjValues(getLanguage().HOME_OVERVIEW_NO_DATA_TEXT_1, status);

    const loggedInOptions = [
        {
            path: `/database/examinations`,
            title: "Examinations",
            variant: "outlined",
            icon: <FeedIcon />
        },
        {
            path: `/database/patients`,
            title: "Patients",
            variant: "outlined",
            icon: <PermContactCalendarIcon />
        }
    ];

    const loggedOutOptions = [
        {
            path: `/home`,
            title: getLanguage().SIDEBAR_OVERVIEW
        },
        {
            path: `/signin`,
            title: getLanguage().BUTTON_SIGNIN,
            variant: "contained"
        }
    ];

    return (
        <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
            {/* Intro text. */}
            <Grid container spacing={1}>
                <Grid item xs={12} className="tour-about-4">
                    <CustomForm title={getLanguage().ABOUT_TITLE} subtitle={getLanguage().ABOUT_SUBTITLE} icon={<InfoIcon />} />
                </Grid>

                <Grid item xs={12}>
                    <PaperText p={2} whiteSpace="pre-wrap" value={getLanguage().ABOUT_TEXT_1} />
                </Grid>

                <Grid item sm={12}>
                    <PaperText p={2} whiteSpace="pre-wrap" value={getLanguage().ABOUT_TEXT_2} />
                </Grid>

                <Grid item sm={12}>
                    <PaperText p={2} whiteSpace="pre-wrap" value={getLanguage().ABOUT_TEXT_3} />
                </Grid>

                <Grid item sm={12}>
                    <PaperText p={2} whiteSpace="pre-wrap" value={getLanguage().ABOUT_TEXT_4} />
                </Grid>

                <Grid item sm={12}>
                    <PaperText p={2} whiteSpace="pre-wrap" value={getLanguage().ABOUT_TEXT_5} />
                </Grid>

                {isLoggedIn && (
                    <Grid item xs={12} className="tour-practice-patient">
                        <CustomForm
                            title="Learn how to use PerioBingo"
                            subtitle="A practice patient for training. It behaves like a real examination and is reset every time you open it."
                            icon={<SchoolIcon />}
                            form={
                                <TransparentFragment
                                    p={2}
                                    value={
                                        <>
                                            <Typography variant="subtitle1" fontWeight={700}>
                                                Trial / Practice Patient
                                            </Typography>
                                            <Typography variant="body2" component="div" sx={{ mt: 0.5, mb: 1.5 }}>
                                                Choose the practice patient from a list of one, then chart missing teeth, plaque, and probing depths the same way you would for a
                                                real patient. At the end you see the same charts and statistics as a saved examination. Nothing is written to the patient records.
                                            </Typography>
                                            <CustomButton {...props} title="Open practice patient" path="/practice" variant="contained" startIcon={<SchoolIcon />} />
                                        </>
                                    }
                                />
                            }
                        />
                    </Grid>
                )}

                <Grid item xs={12} sm={4}>
                    <PaperFragment
                        center
                        value={
                            <>
                                <Typography style={{ whiteSpace: "pre-line" }}>
                                    <img width="95%" src={`${process.env.PUBLIC_URL}/images/logo.png`} alt="..."></img>
                                </Typography>

                                <Typography
                                    sx={{
                                        whiteSpace: "pre-line",
                                        mb: 2,
                                        fontStyle: "italic"
                                    }}>
                                    {getLanguage().ABOUT_TEXT_6}
                                </Typography>
                            </>
                        }
                    />
                </Grid>

                {/* Logged in, and data available. */}
                {isLoggedIn && status && (
                    <Grid item xs={12} sm={8}>
                        <PaperText p={2} value={loggedInOverviewText} />
                        <CustomButton {...props} title="Go to Overview" path="/home" size="small" fullWidth />
                    </Grid>
                )}

                {/* Not logged in, but public data available. */}
                {!isLoggedIn && status && (
                    <Grid item xs={12} sm={8}>
                        <PaperText p={2} value={loggedOutOverviewText} />
                    </Grid>
                )}

                {/* Absolutely no data available. */}
                {!isLoggedIn && !status && (
                    <Grid item xs={12} sm={8}>
                        <PaperText p={2} center bold value={getLanguage().HOME_OVERVIEW_NO_DATA_TEXT_2} />
                    </Grid>
                )}

                <NavigationFooter rows={1} options={props.user.isLoggedIn ? loggedInOptions : loggedOutOptions} />
            </Grid>
        </Container>
    );
}
